/**
 * True when `value` is an http(s) URL whose hostname is dotted with a 2+ char TLD.
 *
 * Parsed with the URL constructor instead of matched against a pattern. The
 * previous regex nested a quantifier over a character class that itself
 * contained "/" -- (\/[\w\/_\-\.\?\=\&\%\#]*)* -- so a URL ending in a run of
 * slashes backtracked exponentially: ~15s to reject a 30-character string, and
 * roughly doubling every character after that. Every check below is linear.
 */
export const isValidHttpUrl = (value) => {
  let parsed;
  try {
    parsed = new URL(String(value ?? "").trim());
  } catch {
    return false;
  }

  if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
    return false;
  }

  // A dotted hostname with a real TLD, matching what the old pattern required
  // (so "http://localhost" stays rejected). Splitting on "." keeps this linear;
  // the per-label test has a single, un-nested quantifier.
  const labels = parsed.hostname.split(".");
  return (
    labels.length >= 2 &&
    labels.at(-1).length >= 2 &&
    labels.every((label) => /^[\w-]+$/.test(label))
  );
};

// `selectedProductFiles` is no longer read — product images became optional —
// but the parameter STAYS. It is positional and `isDraft` sits after it, so
// dropping it would silently shift every caller's last argument and turn a
// published campaign into a draft.
export const validateOverviewStep = (formData, selectedBannerFile, selectedProductFiles, isDraft) => {
  const errors = {};

  const dateRegex = /^(\d{4}-\d{2}-\d{2})$|^([0-2][0-9]|3[0-1])\/(0[1-9]|1[0-2])\/\d{4}$/;

  if (!formData.title?.trim()) {
    errors.title = "Offer title is required.";
  } else if (formData.title < 50) {
    errors.title = "Offer title must be at least 50 characters.";
  }

  if (!formData.offer_description?.trim()) {
    errors.offer_description = "Offer description is required.";
  } else if (formData.offer_description.length < 100) {
    errors.offer_description = "Offer description must be at least 100 characters.";
  } else if (formData.offer_description && formData.offer_description.length > 800) {
    errors.offer_description = "Offer Descrition must be 800 characters";
  }

  if (!formData.product_description?.trim()) {
    errors.product_description = "Product description is required.";
  } else if (formData.product_description.length < 100) {
    errors.product_description = "Product description must be at least 100 characters.";
  } else if (formData.product_description && formData.product_description.length > 800) {
    errors.product_description = "Product description must be 800 characters.";
  }

  if (!isDraft && !formData.banner && !selectedBannerFile) {
    errors.banner = "Banner image is required.";
  }

  // Product images are OPTIONAL — this rule was the only thing making them
  // mandatory. gncollege has always accepted a campaign without any
  // (`ProductImages.barter` is null/blank and the serializer never required
  // the field), so nothing downstream needs one.
  //
  // The upload checks in OfferOverview.js still apply to images that ARE
  // added — at most three, each 150x150. Those are about a file being usable,
  // which is a different question from whether one has to exist.

  if (!formData.offer_category) {
    errors.offer_category = "Offer category is required.";
  }

  if (!isDraft && !formData.meta_description?.trim()) {
    errors.meta_description = "Meta description is required.";
  } else if (!isDraft && formData.meta_description.length < 50) {
    errors.meta_description = "Meta description must be at least 50 characters.";
  } else if (!isDraft && formData.meta_description && formData.meta_description.length > 100) {
    errors.meta_description = "Meta description must be 100 characters.";
  }

  if (!isDraft && !formData.url?.trim()) {
    errors.url = "Offer URL is required.";
  } else if (!isDraft && !isValidHttpUrl(formData.url.trim())) {
    errors.url = "Please enter a valid URL";
  }
  if (!isDraft) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // --- Start Date ---
    if (!formData.start_date) {
      errors.start_date = "Start date is required.";
    } else if (!dateRegex.test(formData.start_date)) {
      errors.start_date = "Start date must be in DD/MM/YYYY or YYYY-MM-DD format.";
    } else {
      let start;
      if (formData.start_date.includes("-")) {
        // format YYYY-MM-DD
        start = new Date(formData.start_date);
      } else {
        // format DD/MM/YYYY
        const [d, m, y] = formData.start_date.split("/");
        start = new Date(`${y}-${m}-${d}`);
      }

      if (isNaN(start)) {
        errors.start_date = "Start date is invalid.";
      } else if (start < today) {
        errors.start_date = "Start date cannot be in the past.";
      }

      // --- End Date ---
      if (!formData.end_date) {
        errors.end_date = "End date is required.";
      } else if (!dateRegex.test(formData.end_date)) {
        errors.end_date = "End date must be in DD/MM/YYYY or YYYY-MM-DD format.";
      } else {
        let end;
        if (formData.end_date.includes("-")) {
          end = new Date(formData.end_date);
        } else {
          const [d, m, y] = formData.end_date.split("/");
          end = new Date(`${y}-${m}-${d}`);
        }

        if (isNaN(end)) {
          errors.end_date = "End date is invalid.";
        } else if (end < start) {
          errors.end_date = "End date cannot be earlier than start date.";
        }
      }
    }
  }

  if (!isDraft && !formData.available_units || isNaN(formData.available_units)) {
    errors.available_units = "Available units must be a valid number.";
  }

  return errors;
};

export const validateTargetingStep = (formData, sections, isDraft) => {
  const errors = {};

  if (!isDraft && (!formData.redemption_steps || formData.redemption_steps.trim() === "")) {
    errors.redemption_steps = "Redemption steps are required.";
  } else if (formData.redemption_steps && formData.redemption_steps.length < 100) {
    errors.redemption_steps = "Redemption steps must be at least 100 characters.";
  } else if (formData.redemption_steps && formData.redemption_steps.length > 800) {
    errors.redemption_steps = "Redemption steps must be 800 characters."
  }

  const sectionErrors = sections.map((section) => {
    const err = {};
    const hasFormat = !!section.format?.label;
    const hasQuantity = !!section.quantities;

    if (!hasFormat) {
      err.format = "Format is required.";
    }
    if (!hasQuantity || isNaN(section.quantities)) {
      err.quantities = "Quantity is required.";
    }

    return Object.keys(err).length > 0 ? err : null;
  });

  const hasValidSection = sections.some(
    (section) => section.format?.label && section.quantities
  );

  if (!hasValidSection) {
    errors.content_requirement = "At least one content requirement (format & quantity) is required.";
  }

  if (sectionErrors.some(Boolean)) {
    errors.sections = sectionErrors;
  }

  if (!Object.values(formData.platforms || {}).some(val => val === true)) {
    errors.platforms = "At least one platform must be selected.";
  }

  if (!formData.audience_type || !formData.audience_type.length) {
    errors.audience_type = "At least one audience type must be selected.";
  }

  if (!isDraft && (!formData.influencer_tier || !formData.influencer_tier.length)) {
    errors.influencer_tier = "At least one influencer tier must be selected.";
  }

  if (!isDraft && (!formData.offer_type || !formData.offer_type.length)) {
    errors.offer_type = "At least one Audience Interest must be selected.";
  }

  if (!isDraft && !formData.gender) {
    errors.gender = "Gender is required";
  }

  return errors;
};

export const validateGuidelinesStep = (formData, referenceLinks, isDraft) => {
  const errors = {};

  const allowedDomains = [
    "drive.google.com",
    "instagram.com",
    "www.instagram.com",
    "facebook.com",
    "www.facebook.com",
    "youtube.com",
    "www.youtube.com",
    "youtu.be"
  ];

  const extractTextFromHTML = (htmlString) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlString;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    errors.email = "Enter a valid email address.";
  }

  if (!formData.phone || !/^\d{10}$/.test(formData.phone)) {
    errors.phone = "Enter a valid 10-digit phone number.";
  }

  if (!formData.contact_name || formData.contact_name.trim() === "") {
    errors.contact_name = "Contact name is required.";
  }

  if (!formData.designation || formData.designation.trim() === "") {
    errors.designation = "Contact designation is required.";
  }

  if (formData.guidelines && formData.guidelines.length > 800) {
    errors.guidelines = "Messaging Guidelines must be 800 characters."
  }

  if (formData.faq && formData.faq.length > 800) {
    errors.faq = "FAQ must be 800 characters."
  }

  if (formData.content_do && formData.content_do.length > 800) {
    errors.content_do = "Content Do's must be 800 characters."
  }

  if (formData.content_dont && formData.content_dont.length > 800) {
    errors.content_dont = "Content Don's must be 800 characters."
  }

  if (isDraft) {
    if (!formData.content_do || formData.content_do.trim() === "") {
      errors.content_do = "Content Do's are required.";
    } else if (formData.content_do.length < 100) {
      errors.content_do = "Content Do's must be at least 100 characters.";
    }

    if (!formData.content_dont || formData.content_dont.trim() === "") {
      errors.content_dont = "Content Don'ts are required.";
    } else if (formData.content_dont.length < 100) {
      errors.content_dont = "Content Don'ts must be at least 100 characters.";
    }
  }

  const rawHashtags = extractTextFromHTML(formData.hashtags || "");

  if (!rawHashtags.trim()) {
    errors.hashtags = "Hashtags are required.";
  } else {
    const tags = rawHashtags.trim().split(/\s+/);
    const invalidTags = tags.filter(tag => !tag.startsWith("#"));
    if (invalidTags.length > 0) {
      errors.hashtags = "Each hashtag must start with a '#' (e.g., #brand #offer)";
    }
  }

  if (!formData.mentions || formData.mentions.trim() === "") {
    errors.mentions = "Mentions are required.";
  }

  const seenLinks = new Set();
  const linkErrors = [];

  referenceLinks.forEach((linkObj, index) => {
    const link = typeof linkObj.link === "string" ? linkObj.link.trim() : "";

    if (!link) {
      linkErrors[index] = "Reference link is required.";
      return;
    }

    if (!isValidHttpUrl(link)) {
      linkErrors[index] = "Invalid URL.";
      return;
    }

    // No try/catch here any more: isValidHttpUrl above has already parsed this
    // exact string with `new URL()`, so this call cannot throw. The old catch
    // arm ("Invalid URL format.") was reachable only when the regex accepted
    // something the parser rejected -- a gap that parsing up front removes.
    const parsedUrl = new URL(link);
    const hostname = parsedUrl.hostname.replace(/^www\./, "");

    if (!allowedDomains.includes(hostname)) {
      linkErrors[index] = "Only Google Drive, Instagram, Facebook, or YouTube links are allowed.";
      return;
    }

    if (seenLinks.has(link)) {
      linkErrors[index] = "Duplicate link.";
      return;
    }

    seenLinks.add(link);
  });

  if (linkErrors.some(Boolean)) {
    errors.reference_links = linkErrors;
  }

  return errors;
};

